import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFieldReview } from './useFieldReview';
import { ReviewMeter } from './ReviewMeter';
import { normalizeMeasurementType, TYPE_COLOR_KEY } from '../../../scouting/pestConfig';
import type { BlockReview, PestCell } from '../../../scouting/reviewModel';
import { naturalCompareBy } from '../../../utils/naturalCompare';
import './review.css';

const TK_COLOR: Record<'inc' | 'lev' | 'cnt', string> = {
  inc: 'var(--blue-500)', lev: 'var(--purple-500)', cnt: 'var(--green-500)',
};

const ReviewMatrixPage: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, pests, current } = useFieldReview();
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  if (error) return <div className="alert alert-danger m-3" role="alert">{error}</div>;
  if (loading) return <div className="p-4 text-muted">{t('fieldScout.review.matrix.title')}…</div>;

  const selected = current.find(b => b.blockId === selectedBlockId) || null;
  const rows = [...current].sort(naturalCompareBy(b => b.blockId));
  const cellOf = (b: BlockReview, pestId: string): PestCell | undefined => b.cells.find(c => c.pestId === pestId);

  return (
    <div className="rev-page">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <div>
          <h1 className="h4 mb-0">{t('fieldScout.review.matrix.title')}</h1>
          <div className="text-muted small">{t('fieldScout.review.matrix.sub')}</div>
        </div>
        <div className="ms-auto">
          <Link className="btn btn-outline-secondary btn-sm" to="/scouting/review">← {t('fieldScout.review.matrix.backToSummary')}</Link>
        </div>
      </div>

      {current.length === 0 ? (
        <div className="card card-pad text-muted">{t('fieldScout.review.matrix.empty')}</div>
      ) : (
        <>
          <div className="card rev-hm-wrap">
            <table className="rev-hm">
              <thead>
                <tr>
                  <th className="rev-hm__blk">{t('fieldScout.review.summary.colBlock')}</th>
                  {pests.map(p => {
                    const ck = TYPE_COLOR_KEY[normalizeMeasurementType(p.measurementType)];
                    return (
                      <th key={p.pestId}>
                        <span className="rev-hm__tk" style={{ background: TK_COLOR[ck] }} />
                        <span className="rev-hm__cd">{p.code}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map(b => (
                  <tr key={b.blockId} className={b.blockId === selectedBlockId ? 'rev-hm__sel' : ''}>
                    <td className="rev-hm__blk">
                      <button type="button" className="btn btn-link p-0" onClick={() => setSelectedBlockId(b.blockId)}>
                        <b>{b.blockId}</b> <span className="text-muted small">{b.variety}</span>
                      </button>
                    </td>
                    {pests.map(p => {
                      const c = cellOf(b, p.pestId);
                      const cls = !c || !c.inProtocol || !c.flag ? 'na' : c.flag;
                      const inProto = !!(c && c.inProtocol && c.flag);
                      return (
                        <td key={p.pestId} data-testid={`hm-cell-${b.blockId}-${p.pestId}`}
                            className={`rev-hm__cell rev-hm__cell--${cls}`}
                            onClick={() => setSelectedBlockId(b.blockId)}
                            aria-label={`${b.blockId} ${p.code} ${inProto ? c!.display : t('fieldScout.review.matrix.legendNotInProtocol')}`}>
                          {inProto ? c!.display : '·'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rev-hm-legend">
            <span><i className="rev-hm__sw rev-hm__sw--exceeded" />{t('fieldScout.review.exceeded')}</span>
            <span><i className="rev-hm__sw rev-hm__sw--approaching" />{t('fieldScout.review.approaching')}</span>
            <span><i className="rev-hm__sw rev-hm__sw--below" />{t('fieldScout.review.below')}</span>
            <span><i className="rev-hm__sw rev-hm__sw--na" />{t('fieldScout.review.matrix.legendNotInProtocol')}</span>
            <span className="ms-auto">{t('fieldScout.review.matrix.clickHint')} ↓</span>
          </div>

          {selected && (
            <div className="card card-pad mt-3">
              <div className="d-flex align-items-baseline gap-2 mb-1">
                <span className="fw-bold fs-5">{selected.orchardName} · {selected.variety}</span>
                {selected.counts.exceeded > 0 && <span className="rev-pill rev-pill--exceeded">{t('fieldScout.review.summary.kOver', { count: selected.counts.exceeded })}</span>}
                <Link className="ms-auto small" to={`/scouting/review?block=${encodeURIComponent(selected.blockId)}`}>{t('fieldScout.review.summary.open')} →</Link>
              </div>
              <div className="section-label">{t('fieldScout.review.matrix.drillHeading')}</div>
              <div className="rev-dstrip" data-testid="matrix-detail-strip">
                {selected.cells.filter(c => c.flag === 'exceeded' || c.flag === 'approaching').map(c => (
                  <div key={c.code} className="rev-dcard">
                    <div className="d-flex justify-content-between align-items-baseline">
                      <span className="fw-bold">{c.code}</span>
                      <span className={`rev-prow__val--${c.flag}`}>{c.display}</span>
                    </div>
                    <div className="text-muted small">{c.name}</div>
                    <ReviewMeter value={c.value} threshold={c.threshold} flag={c.flag!} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewMatrixPage;
